import React, { useEffect, useMemo, useState } from "react";
import { Button, ControlLabel, FormGroup, Glyphicon, ListGroupItem } from "react-bootstrap";
import Select from "react-select";

import * as Api from "../../../../../api/cp/eventViewingRestriction";
import { viewTypeRestrictionsLists } from "../models";
import HelpHintButton from "components/HelpHintButton";
import DeleteConfirmOverlay from "../../../UI/DeleteConfirmOverlay";

const useCountries = (eventKey, filter) => {
    const [dictionaryOfCountries, setDictionaryOfCountries] = useState([]);
    const [selectedCountries, setSelectedCountries] = useState([]);

    useEffect(fetchCountries, []);

    useEffect(() => {
        if (filter) fetchSelectedCountries();
    }, [eventKey, filter]);

    async function fetchCountries() {
        try {
            const response = await Api.getCountriesList();
            setDictionaryOfCountries(response || []);
        } catch (error) {
            console.error(error);
        }
    }

    async function fetchSelectedCountries() {
        try {
            const response = await Api.getCountriesListByType(eventKey, filter);
            setSelectedCountries(response || []);
        } catch (error) {
            console.error(error);
        }
    }

    return { dictionaryOfCountries, selectedCountries, setSelectedCountries };
};

const AccessByCountrySettings = ({ locale, eventKey, filter, isSaving, onSaveFilter }) => {
    const [country, setCountry] = useState('');
    const { dictionaryOfCountries, selectedCountries, setSelectedCountries } = useCountries(eventKey, filter);

    const countriesList = useMemo(() => {
        return Object.values(dictionaryOfCountries)?.map((country) => ({
            value: country.code,
            label: locale.field(country, 'name'),
        }));
    }, [dictionaryOfCountries]);

    const countriesOptions = useMemo(() => {
        if (!countriesList.length) return [];

        return Object.values(countriesList).filter(({ value }) => {
            return !selectedCountries.find(({ code }) => code === value);
        });

    }, [countriesList, selectedCountries]);

    async function handleAddCountry() {
        try {
            const response = await Api.addCountry(eventKey, { type: filter, code: country.code });
            setSelectedCountries((prevState) => ([...prevState, response]));
            setCountry('');
        } catch (error) {
            console.error(error);
        }
    }

    async function handleDeleteCountry(id) {
        try {
            await Api.deleteCountry(id);
            setSelectedCountries((prevState) => prevState.filter((country) => country.id !== id));
        } catch (error) {
            console.error(error);
        }
    }

    function getCountryByCode(code) {
        return locale.field(dictionaryOfCountries?.[code], 'name');
    }

    return (
        <div>
            <ControlLabel>
                {locale('settings.viewTypeRestriction.byCountry.title')}
                <HelpHintButton>
                    {locale('settings.viewTypeRestriction.byCountry.hint')}
                </HelpHintButton>
            </ControlLabel>
            <FormGroup style={{ display: 'grid', gridTemplateColumns: '200px 1fr 200px', columnGap: '12px' }}>
                <Select
                    value={viewTypeRestrictionsLists.find(({ value }) => value === filter)}
                    options={viewTypeRestrictionsLists}
                    isDisabled={isSaving}
                    placeholder={locale('settings.viewTypeRestriction.selectType')}
                    onChange={({ value }) => onSaveFilter({ geoFilter: value })}
                />
                <Select
                    value={country ? { value: country.code, label: locale.field(country, 'name') } : ''}
                    options={countriesOptions}
                    isDisabled={isSaving}
                    isSearchable
                    onChange={({ value }) => setCountry(dictionaryOfCountries?.[value])}
                />
                <Button
                    disabled={!filter || !country || isSaving}
                    onClick={handleAddCountry}
                >
                    <Glyphicon glyph="plus" /> {locale('settings.viewTypeRestriction.byCountry.button')}
                </Button>
            </FormGroup>
            {selectedCountries?.map(({ code, id }) => (
                <ListGroupItem
                    key={id}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 10px' }}
                >
                    {getCountryByCode(code)}
                    <DeleteConfirmOverlay
                        title={locale('settings.viewTypeRestriction.byCountry.confirm')}
                        buttonText={locale('message.control.delete')}
                        onConfirm={() => handleDeleteCountry(id)}
                    >
                        <Button>
                            <Glyphicon glyph="trash" />
                        </Button>
                    </DeleteConfirmOverlay>
                </ListGroupItem>
            ))}
        </div>
    );
};

export default AccessByCountrySettings;
